import type { GettextInterface } from "../../I18n";

import { ContentProblem, REPORT_JOB_FEEDBACK_MAX_LENGTH } from "./ContentProblem";

export type ReportJobOptionItem = {
  value: ContentProblem;
  label: string;
};

/**
 * Shared submit-enabled rule for report-job forms.
 * Requires a reason and/or non-empty moreInfo, within the feedback length limit.
 */
export function canSubmitReportJob({
  reason,
  moreInfo,
  feedbackMaxLength = REPORT_JOB_FEEDBACK_MAX_LENGTH,
}: {
  reason: string;
  moreInfo: string;
  feedbackMaxLength?: number;
}): boolean {
  return moreInfo.length <= feedbackMaxLength && (reason !== "" || moreInfo.trim().length > 0);
}

export function labelForContentProblem(gt: GettextInterface, problem: ContentProblem): string {
  switch (problem) {
    case ContentProblem.EXPIRED:
      return gt.pgettext("report job expired text", "It is expired");
    case ContentProblem.FAKE:
      return gt.pgettext("report job fake text", "It seems like a fake job");
    case ContentProblem.INACCURATE:
      return gt.pgettext("report job inaccurate text", "It is inaccurate");
    case ContentProblem.OFFENSIVE:
      return gt.pgettext("report job offensive text", "It is offensive, discriminatory");
    case ContentProblem.ADVERTISEMENT:
      return gt.pgettext("report job advertisement text", "It is an advertisement");
    case ContentProblem.TECHNICAL_ISSUE:
      return gt.pgettext(
        "report job technical issue text",
        "It shows errors when viewing or applying",
      );
    case ContentProblem.NOT_HIRING:
      return gt.pgettext(
        "report job not hiring text",
        "It doesn't seem like they're hiring for this role",
      );
    case ContentProblem.PHOTO:
      return gt.pgettext("report job photo", "Images contain inappropriate content");
    case ContentProblem.OTHER:
      return gt.pgettext("report job other text", "Other");
  }
}

/**
 * Canonical report-job reason list, with optional photo / not-hiring reporting.
 * When `showNotHiringReason` is true (jcs_report_job_tst > 0), inserts NOT_HIRING at index 2.
 */
export function getReportJobOptions(
  gt: GettextInterface,
  showPhotoLabel = false,
  showNotHiringReason = false,
): ReportJobOptionItem[] {
  const contentProblems = [
    ContentProblem.OFFENSIVE,
    ContentProblem.FAKE,
    ContentProblem.INACCURATE,
    ContentProblem.ADVERTISEMENT,
  ];
  if (showNotHiringReason) {
    contentProblems.splice(2, 0, ContentProblem.NOT_HIRING);
  }
  if (showPhotoLabel) {
    contentProblems.push(ContentProblem.PHOTO);
  }
  contentProblems.push(ContentProblem.OTHER);

  return contentProblems.map((problem) => ({
    value: problem,
    label: labelForContentProblem(gt, problem),
  }));
}
